/**
 * Uitklapbare topnav.
 *
 * De pil bovenin morpht: één GSAP-timeline animeert breedte en hoogte van .topnav__inner
 * en staggert de items naar binnen. De helft na addPause() is de sluitbeweging, zodat een
 * klik halverwege het openen netjes terugdraait in plaats van te springen.
 *
 * Wat er opengaat verschilt per schermbreedte:
 * - vanaf lg staat het menu al in de balk en klapt een menu-item zijn submenu uit;
 * - daaronder opent de hamburger het volledige menu.
 *
 * In beide gevallen is het dezelfde beweging op hetzelfde element -- alleen de inhoud van
 * het paneel verschilt, en daarmee de hoogte die measure() uitrekent.
 */

import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { getLenis } from './smooth-scroll';

gsap.registerPlugin(CustomEase);

if (!CustomEase.get('osmo')) {
  CustomEase.create('osmo', 'M0,0 C0.625,0.05 0,1 1,1');
}

export default function initTopNav() {
  const nav = document.querySelector('[data-topnav-init]');

  if (!nav) {
    return;
  }

  const inner = nav.querySelector('[data-topnav-inner]');
  const bar = nav.querySelector('[data-topnav-bar]');
  const panel = nav.querySelector('[data-topnav-panel]');
  const toggle = nav.querySelector('[data-topnav-toggle]');
  const mobile = nav.querySelector('[data-topnav-mobile]');

  if (!inner || !bar || !panel) {
    return;
  }

  const submenus = [...nav.querySelectorAll('[data-topnav-submenu]')];
  const submenuToggles = [...nav.querySelectorAll('[data-topnav-submenu-toggle]')];
  const mobileToggles = [...nav.querySelectorAll('[data-topnav-mobile-toggle]')];

  let isOpen = false;
  let openId = null;
  let enterEnd = 0;
  let dimensions = { closedW: 0, closedH: 0, openW: 0, openH: 0 };
  let tl;

  // Alleen wat nu zichtbaar is mag meedoen aan de stagger; de andere submenu's staan
  // op display:none en zouden anders als onzichtbare items meetellen.
  function currentReveals() {
    const zichtbaar = openId
      ? nav.querySelector(`[data-topnav-submenu="${openId}"]`)
      : mobile;

    return zichtbaar ? zichtbaar.querySelectorAll('[data-topnav-reveal]') : [];
  }

  // De open maten staan in CSS-variabelen; even naar 'auto' zetten geeft de echte
  // hoogte van het uitgeklapte paneel terug, daarna zetten we alles weer terug.
  function measure() {
    const w = inner.style.width;
    const h = inner.style.height;

    inner.style.width = 'var(--open-width)';
    inner.style.height = 'auto';

    const openW = inner.offsetWidth;
    const openH = inner.offsetHeight;

    // Het paneel even uit de stroom halen voor de gesloten meting. Staat er een submenu
    // open, dan bepalen de kaartjes anders de breedte en landt het sluiten op een te
    // brede pil -- met een gat rechts naast de knoppen tot gevolg.
    const paneelDisplay = panel.style.display;

    panel.style.display = 'none';
    inner.style.width = 'var(--closed-width)';
    inner.style.height = 'auto';

    // De auto-breedte van de pil komt een paar pixels korter uit dan wat de balk
    // werkelijk nodig heeft -- width: 100% op de balk maakt de intrinsieke meting
    // cyclisch. De scrollWidth van de balk zelf is wel de echte maat, dus de pil
    // wordt nooit smaller dan dat; anders valt de laatste knop buiten de overflow.
    // scrollWidth telt de linkerpadding wel mee en de rechter niet, dus die komt er
    // los bij -- anders plakt de laatste knop tegen de rand van de pil.
    const barPadRight = parseFloat(getComputedStyle(bar).paddingRight) || 0;
    const closedW = Math.max(inner.offsetWidth, Math.ceil(bar.scrollWidth + barPadRight));

    panel.style.display = paneelDisplay;
    inner.style.width = w;
    inner.style.height = h;

    // Bij flinke browserzoom wint de 92vw-tak van --open-width en komt het paneel
    // smaller uit dan de balk zelf; met overflow: hidden valt de laatste knop dan
    // buiten beeld. De pil gaat daarom nooit smaller open dan hij dicht al is.
    return { closedW, closedH: bar.offsetHeight, openW: Math.max(openW, closedW), openH };
  }

  function applyClosed() {
    gsap.set(inner, { width: dimensions.closedW, height: dimensions.closedH });
    publiceerMaat();
  }

  /**
   * De maat en de plek van de gesloten pil doorgeven aan de rest van de pagina.
   *
   * De sollicitatiebalk op een vacature hangt eronder en moet er even breed als zijn. Die
   * breedte valt daar niet uit te rekenen: hij komt uit een meting hier, en hangt af van
   * de menu-items en het lettertype. Dus publiceren we hem, en hermeten we mee bij een
   * resize en zodra de webfonts binnen zijn.
   *
   * De onderkant gaat als gemeten waarde mee en niet als 'afstand van boven plus hoogte':
   * die afstand verschilt per breakpoint (1em, vanaf md 1.5em) en dat naschrijven is precies
   * hoe het mis ging -- de balk kwam 8px te hoog en dook onder de pil.
   */
  function publiceerMaat() {
    const root = document.documentElement;

    root.style.setProperty('--topnav-w', `${dimensions.closedW}px`);
    root.style.setProperty('--topnav-bottom', `${Math.round(inner.getBoundingClientRect().bottom)}px`);
  }

  function buildTimeline(reveals) {
    if (tl) {
      tl.kill();
    }

    const barTop = toggle ? toggle.querySelector('.topnav__toggle-bar.is--top') : null;
    const barBot = toggle ? toggle.querySelector('.topnav__toggle-bar.is--btm') : null;

    tl = gsap.timeline({
      paused: true,
      defaults: { ease: 'osmo', easeReverse: 'power2.inOut' },
    });

    tl.to(inner, {
      width: () => dimensions.openW,
      height: () => dimensions.openH,
      duration: 0.65,
    }, 0);

    if (barTop && barBot) {
      tl.to(barTop, {
        y: '0.175em',
        rotation: 45,
        duration: 0.4,
        ease: 'back.out(2)',
        easeReverse: 'power3.out',
      }, 0.05)

        .to(barBot, {
          y: '-0.175em',
          rotation: -45,
          duration: 0.4,
          ease: 'back.out(2)',
          easeReverse: 'power3.out',
        }, 0.05);
    }

    tl.set(panel, { autoAlpha: 1 }, 0.1);

    if (reveals.length) {
      tl.fromTo(reveals, {
        autoAlpha: 0,
        yPercent: 100,
      }, {
        autoAlpha: 1,
        yPercent: 0,
        duration: 0.6,
        stagger: 0.03,
      }, 0.1);
    }

    enterEnd = tl.duration();
    tl.addPause();

    // Sluiten: bewust geen reverse van het bovenstaande, maar een kortere eigen beweging.
    if (reveals.length) {
      tl.to(reveals, { autoAlpha: 0, yPercent: 10, duration: 0.25, stagger: { each: 0.01, from: 'end' } });
    }

    tl.to(inner, {
      width: () => dimensions.closedW,
      height: () => dimensions.closedH,
      duration: 0.45,
      ease: 'power3.inOut',
    }, reveals.length ? '<' : 0);

    if (barTop && barBot) {
      tl.to([barTop, barBot], { y: 0, rotation: 0, duration: 0.3, ease: 'power3.in' }, '<');
    }

    tl.set(panel, { autoAlpha: 0 });
  }

  function setState(open) {
    isOpen = open;
    nav.setAttribute('data-topnav-open', String(open));
    // Ook op de root: wat onder de pil hangt moet zich kunnen wegduiken als die uitklapt,
    // en dat staat elders in de DOM -- buiten bereik van een selector vanaf de nav.
    document.documentElement.setAttribute('data-topnav-open', String(open));
    panel.setAttribute('aria-hidden', String(!open));

    if (toggle) {
      toggle.setAttribute('aria-expanded', String(open && !openId));
      toggle.setAttribute('aria-label', open ? 'Menu sluiten' : 'Menu openen');
    }

    submenuToggles.forEach((knop) => {
      const actief = open && knop.dataset.topnavSubmenuToggle === openId;
      knop.setAttribute('aria-expanded', String(actief));
    });

    submenus.forEach((sub) => {
      sub.classList.toggle('is--open', open && sub.dataset.topnavSubmenu === openId);
    });

    if (mobile) {
      mobile.style.display = openId ? 'none' : '';
    }
  }

  // Alle uitgeklapte groepen terug naar dicht, zonder animatie. Wordt bij het sluiten
  // gedaan zodat het menu de volgende keer weer als overzicht opent en niet halverwege
  // iemands vorige zoektocht.
  function resetMobileSubs() {
    mobileToggles.forEach((knop) => {
      const sub = nav.querySelector(`[data-topnav-mobile-sub="${knop.dataset.topnavMobileToggle}"]`);

      knop.setAttribute('aria-expanded', 'false');

      if (sub) {
        gsap.killTweensOf(sub);
        gsap.set(sub, { height: 0 });
      }
    });
  }

  /**
   * Een groep in het mobiele menu open- of dichtklappen. De pil eromheen is op een vaste
   * hoogte gezet door de openingsanimatie, dus die moet mee: eerst de groep op zijn
   * eindhoogte zetten en de pil opnieuw meten, daarna beide vanaf de oude maat animeren.
   *
   * @param {string} id
   */
  function toggleMobileSub(id) {
    const knop = nav.querySelector(`[data-topnav-mobile-toggle="${id}"]`);
    const sub = nav.querySelector(`[data-topnav-mobile-sub="${id}"]`);

    if (!knop || !sub) {
      return;
    }

    const open = knop.getAttribute('aria-expanded') !== 'true';

    knop.setAttribute('aria-expanded', String(open));

    gsap.killTweensOf(sub);

    const vanaf = sub.offsetHeight;

    gsap.set(sub, { height: open ? 'auto' : 0 });

    const naar = sub.offsetHeight;

    // Meten met de eindstand erin; measure() zet de pil zelf netjes terug.
    dimensions = measure();

    gsap.set(sub, { height: vanaf });

    gsap.to(sub, {
      height: naar,
      duration: 0.4,
      ease: 'power2.inOut',
      onComplete: () => {
        if (open) {
          gsap.set(sub, { height: 'auto' });
        }
      },
    });

    gsap.to(inner, { height: dimensions.openH, duration: 0.4, ease: 'power2.inOut' });
  }

  /**
   * @param {boolean} open
   * @param {string|null} id  submenu dat open moet; null = het mobiele menu
   */
  function animate(open, id = null) {
    const wisselt = open && isOpen && id !== openId;

    // Van het ene submenu naar het andere: eerst dicht, dan het nieuwe open. Anders
    // zou de pil naar de verkeerde hoogte morphen, want die is per submenu anders.
    if (wisselt) {
      animate(false);
      window.setTimeout(() => animate(true, id), 260);

      return;
    }

    openId = open ? id : null;
    setState(open);

    if (open) {
      // Vanaf hier telt de scrollafstand; zie onScroll.
      scrollAnker = window.scrollY;

      // Opnieuw meten vlak voor het openen: de hoogte hangt af van welk submenu
      // openstaat, van webfonts en van de breakpoint. Meten is goedkoop, dit scheelt
      // een half uitgeklapte pil.
      dimensions = measure();
      buildTimeline(currentReveals());
      tl.timeScale(1).restart();

      return;
    }

    resetMobileSubs();

    if (!tl) {
      return;
    }

    if (tl.time() < enterEnd) {
      tl.timeScale(1).reverse();
    } else {
      tl.timeScale(1).play();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen) {
      animate(false);

      if (toggle) {
        toggle.focus();
      }
    }
  }

  // Klik buiten de balk sluit het submenu; anders blijft het openstaan terwijl je
  // verderop op de pagina klikt.
  function onDocumentClick(e) {
    if (isOpen && !nav.contains(e.target)) {
      animate(false);
    }
  }

  // Doorscrollen met een uitgeklapt submenu voelt raar: de pil blijft boven de pagina
  // hangen terwijl je ergens anders naartoe leest. Niet bij de eerste pixel dichtklappen
  // -- een tik tegen het trackpad is nog geen bedoeling -- maar zodra er een balkhoogte
  // voorbij is. Dat is ver genoeg om als 'ik ga verder' te lezen en kort genoeg om niet
  // te blijven hangen.
  //
  // Alleen voor de submenu's op desktop. Het mobiele menu is de hele navigatie en scrolt
  // zelf; daar is scrollen juist het doorlezen van de lijst, en dan is dichtklappen het
  // laatste wat je wil.
  let scrollAnker = 0;

  // Zodra de pagina meescrollt krijgt de pil een hairline; boven aan de pagina zweeft hij
  // vrij en hoeft dat niet. Een paar pixels speling, anders knippert hij bij een tik tegen
  // het trackpad.
  function markeerScroll() {
    nav.dataset.topnavScrolled = window.scrollY > 8 ? 'true' : 'false';
  }

  function onScroll() {
    markeerScroll();

    if (!isOpen || !openId) {
      return;
    }

    if (Math.abs(window.scrollY - scrollAnker) > dimensions.closedH) {
      animate(false);
    }
  }

  let resizeTimer;

  function onResize() {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => {
      if (isOpen) {
        animate(false);
      }

      dimensions = measure();
      applyClosed();
    }, 150);
  }

  dimensions = measure();
  applyClosed();
  setState(false);
  markeerScroll();
  gsap.set(panel, { autoAlpha: 0 });

  // De gesloten maat verandert mee met het logo en de webfonts; hermeten zodra die
  // binnen zijn, anders blijft de pil op de fallback-maten staan.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      if (!isOpen) {
        dimensions = measure();
        applyClosed();
      }
    });
  }

  if (toggle) {
    toggle.addEventListener('click', () => animate(!isOpen || Boolean(openId), null));
  }

  submenuToggles.forEach((knop) => {
    knop.addEventListener('click', () => {
      const id = knop.dataset.topnavSubmenuToggle;

      animate(!(isOpen && openId === id), id);
    });
  });

  mobileToggles.forEach((knop) => {
    knop.addEventListener('click', () => toggleMobileSub(knop.dataset.topnavMobileToggle));
  });

  document.addEventListener('keydown', onKeydown);
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('resize', onResize);

  // Twee bronnen, want er is er altijd precies een die levert. Lenis dempt de scroll
  // en geeft zijn eigen 'scroll' af -- dezelfde haak waar smooth-scroll.js ScrollTrigger
  // aan hangt, en daarmee de bron waar de rest van de site ook op draait. Wie minder
  // beweging heeft gevraagd krijgt geen Lenis; dan is het native event de enige bron.
  // initSmoothScroll() draait voor initTopNav() in app.js, dus de instantie staat er al.
  const lenis = getLenis();

  if (lenis) {
    lenis.on('scroll', onScroll);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
}
